import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { Boondock, Review, ReviewType, Schedule, User } from '@curbnturf/entities';
import { IReviewForm, TrimPipe } from '@curbnturf/form-helpers';
import { RatingFacade } from '@curbnturf/rating';
import * as ReviewsActions from '@curbnturf/review';
import { ReviewsFacade } from '@curbnturf/review/src/lib/+state/reviews.facade';
import { ScheduleFacade } from '@curbnturf/schedule/src/lib/+state/schedule.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { DateTime } from 'luxon';
import { Observable, Subscription } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'curbnturf-rating-boondock-public-review',
  templateUrl: './rating-boondock-public-review.component.html',
  styleUrls: ['./rating-boondock-public-review.component.scss'],
  standalone: true,
  imports: [FormsModule, IonicModule, ReactiveFormsModule, RouterModule, NgIf, AsyncPipe, TrimPipe],
})
export class RatingBoondockPublicReviewComponent implements OnInit, OnDestroy {
  @Output() savedReview: EventEmitter<void> = new EventEmitter<void>();

  @Output() completeReview: EventEmitter<void> = new EventEmitter<void>();

  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];
  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;
  user: User;

  reviewForm: FormGroup<IReviewForm>;
  schedule: Schedule | null;
  scheduleConfirmationCode: string | undefined;
  caretakerSites: number[];

  private appContext: boolean = false;

  get isCaretaker() {
    return !!(this.boondockId && this.caretakerSites.includes(this.boondockId));
  }

  constructor(
    private updates$: Actions,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private navCtrl: NavController,
    private ratingFacade: RatingFacade,
    private reviewsFacade: ReviewsFacade,
    private scheduleFacade: ScheduleFacade,
    private boondockFacade: BoondockFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit(): void {
    this.route.data.subscribe((data: { appContext: boolean }) => {
      this.appContext = data.appContext || false;
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.ratingFacade.caretakerSites$.subscribe((sites) => {
        this.caretakerSites = sites;
      }),
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
        }
      }),
      this.route.data.subscribe((data: { appContext: boolean }) => {
        this.appContext = data.appContext || false;
      }),
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
      }),
      this.scheduleFacade.selected$.subscribe((schedule) => {
        this.schedule = schedule;
        this.scheduleConfirmationCode = schedule?.confirmationCode;
      }),
    );

    this.reviewForm = this.fb.group<IReviewForm>({
      review: this.fb.control('', [Validators.required, Validators.minLength(10), Validators.maxLength(5000)]),
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  submitReview(emit = true) {
    this.reviewForm.get('review')?.markAsTouched();
    this.reviewForm.get('privateMessage')?.markAsTouched();

    if (this.reviewForm.dirty && this.reviewForm.valid) {
      const review = new Review({
        syncId: uuidv4(),
        reviewType: ReviewType.boondock,
        review: this.reviewForm.value.review || '',
        privateMessage: this.reviewForm.value.privateMessage || '',
        boondockId: this.boondockId,
        // scheduleCode: this.schedule?.confirmationCode, // @todo maybe link to the schedule in the future?
        creator: this.user,
        creatorId: this.user.id || 0,
        created: DateTime.now().toMillis(),
      });

      if (emit) {
        // Make the subscription before sending the request to make sure we don't miss anything.
        this.subscriptions.push(
          this.updates$.pipe(ofType(ReviewsActions.createBoondockReviewSuccess)).subscribe(async () => {
            // Emit event for web and navigate for app.
            if (this.appContext) {
              await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'activities']);
            } else {
              this.savedReview.emit();
            }
          }),
        );
      }

      this.reviewsFacade.createBoondockReview(review, this.boondockId || 0);
    }
  }

  onNavigateBack(event: MouseEvent) {
    event.preventDefault();
    this.navigateBack.emit();
  }

  onSaveAndExit() {
    this.submitReview();
  }

  async onCompleteReview() {
    this.subscriptions.push(
      this.updates$.pipe(ofType(ReviewsActions.createBoondockReviewSuccess)).subscribe(async () => {
        // Emit event for web and navigate for app.
        if (this.appContext) {
          await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'complete']);
        } else {
          this.completeReview.emit();
        }
      }),
    );

    this.submitReview(false);
  }
}
